/**
 * Edit marketing banner configuration row.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardBody } from '@/components/common/Card';
import HasPermission from '@/components/common/HasPermission';
import { MarketingBannerForm } from '@/components/marketingBanners/MarketingBannerForm';
import { marketingBannerService } from '@/app/services/marketingBannerService';
import type { CreateMarketingBannerRequest } from '@/app/types/marketingBanners';
import { logger } from '@/app/utils/logger';
import { normalizeMarketingBannerRequest } from '@/app/utils/normalizeMarketingBannerRequest';

import './MarketingBannerEdit.scss';

function recordToForm(record: {
  name: string;
  priority: number;
  status: CreateMarketingBannerRequest['status'];
  source_type: CreateMarketingBannerRequest['source_type'];
  static_content?: CreateMarketingBannerRequest['static_content'];
  external_api?: CreateMarketingBannerRequest['external_api'];
}): CreateMarketingBannerRequest {
  return {
    name: record.name,
    priority: record.priority,
    status: record.status,
    source_type: record.source_type,
    static_content: record.static_content ?? null,
    external_api: record.external_api ?? null,
  };
}

export const MarketingBannerEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateMarketingBannerRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const banner = await marketingBannerService.getBannerById(id);
        if (!banner) {
          setError('Marketing banner not found.');
          setFormData(null);
          return;
        }
        setFormData(recordToForm(banner));
      } catch (err) {
        logger.error('Failed to load marketing banner', { error: err, id });
        setError('Failed to load marketing banner.');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !formData) return;
    setSaving(true);
    setError(null);
    try {
      await marketingBannerService.updateBanner(id, normalizeMarketingBannerRequest(formData));
      navigate('/marketing-banners');
    } catch (err: unknown) {
      logger.error('Failed to update marketing banner', { error: err, id });
      const detail =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
          'string'
          ? (err as { response: { data: { detail: string } } }).response.data.detail
          : 'Failed to update marketing banner.';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !formData) return;
    if (
      !window.confirm(
        `Delete marketing banner "${formData.name}" (priority ${formData.priority})? This cannot be undone.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      const deleted = await marketingBannerService.deleteBanner(id);
      if (deleted) {
        navigate('/marketing-banners');
      } else {
        setError('Marketing banner not found.');
      }
    } catch (err) {
      logger.error('Failed to delete marketing banner', { error: err, id });
      setError('Failed to delete marketing banner.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <p className="marketing-banner-edit__loading">Loading…</p>;
  }

  if (!formData) {
    return (
      <div className="marketing-banner-edit__error" role="alert">
        {error ?? 'Marketing banner not found.'}
      </div>
    );
  }

  return (
    <form className="marketing-banner-edit" onSubmit={(e) => void handleSubmit(e)}>
      {error && (
        <div className="marketing-banner-edit__error" role="alert">
          {error}
        </div>
      )}
      <MarketingBannerForm value={formData} onChange={setFormData} disabled={saving || deleting} />
      <Card>
        <CardBody>
          <div className="marketing-banner-edit__actions">
            <HasPermission permission="marketing_banner:write">
              <button
                type="button"
                className="btn-danger"
                onClick={() => void handleDelete()}
                disabled={saving || deleting}
              >
                {deleting ? 'Deleting…' : 'Delete banner'}
              </button>
            </HasPermission>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/marketing-banners')}
              disabled={saving || deleting}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving || deleting}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </CardBody>
      </Card>
    </form>
  );
};

export default MarketingBannerEdit;
